import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ArrowLeft, Loader2, CheckCircle2, Phone, User, Mail, MapPin, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { OLLIE_IMAGES } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LeadCaptureFlow = ({ productType, productName, postcode, address, enquiryId, onBack, onComplete }) => {
  const [step, setStep] = useState('details'); // 'details', 'otp', 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [verifiedMobile, setVerifiedMobile] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateMobile = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10 && cleaned.startsWith('04');
  };

  const handleSendOtp = async () => {
    setError('');

    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    if (!lastName.trim()) {
      setError('Please enter your last name');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!validateMobile(mobile)) {
      setError('Please enter a valid Australian mobile number (04XX XXX XXX)');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/otp/send`, {
        mobile: mobile.replace(/\D/g, '')
      });

      // Track journey step - OTP sent
      if (enquiryId) {
        axios.patch(`${BACKEND_URL}/api/enquiries/${enquiryId}/journey`, { step: 'otp_sent' }).catch(() => {});
      }

      setVerifiedMobile(mobile);
      setStep('otp');
      toast.success('Verification code sent!', {
        description: `We've sent a code to ${mobile}`,
      });
    } catch (err) {
      console.error('OTP send error:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/otp/verify`, {
        mobile: verifiedMobile.replace(/\D/g, ''),
        otp: otp
      });

      // Track journey step - OTP verified
      if (enquiryId) {
        axios.patch(`${BACKEND_URL}/api/enquiries/${enquiryId}/journey`, { step: 'otp_verified' }).catch(() => {});
      }

      // Save contact details to the enquiry (this will mark journey as completed)
      await axios.patch(`${BACKEND_URL}/api/enquiries/${enquiryId}/contact`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        mobile: verifiedMobile.replace(/\D/g, '')
      });

      setStep('success');
      toast.success('Details submitted!', {
        description: 'Our team will contact you shortly.',
      });

    } catch (err) {
      console.error('Verification error:', err);
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatMobile = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);
    if (cleaned.length >= 6) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
    }
    return cleaned;
  };

  // Success screen
  if (step === 'success') {
    return (
      <div className="text-center py-8">
        {/* Ollie celebrating */}
        <div className="relative inline-block mb-6">
          <img 
            src={OLLIE_IMAGES.waving} 
            alt="Ollie the Owl" 
            className="w-32 h-32 object-contain mx-auto"
          />
          <div className="absolute -top-2 -right-2">
            <CheckCircle2 className="w-10 h-10 text-green-500 bg-white rounded-full" />
          </div>
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Thank You, {firstName}!
        </h2>

        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 mb-6 max-w-md mx-auto">
          <p className="text-gray-700 mb-4">
            We've received your enquiry for <span className="font-semibold text-cyo-orange">{productName}</span>.
          </p>
          
          <div className="flex items-center justify-center gap-2 text-gray-600 mb-4">
            <Clock className="w-5 h-5 text-cyo-orange" />
            <span className="font-medium">One of our team members will be in touch soon!</span>
          </div>

          <div className="bg-white rounded-xl p-4 text-left space-y-2">
            <p className="text-sm text-gray-500">Your details:</p>
            <p className="text-sm"><span className="text-gray-500">Name:</span> <span className="font-medium">{firstName} {lastName}</span></p>
            <p className="text-sm"><span className="text-gray-500">Email:</span> <span className="font-medium">{email}</span></p>
            <p className="text-sm"><span className="text-gray-500">Mobile:</span> <span className="font-medium">{formatMobile(verifiedMobile)}</span></p>
            {address && <p className="text-sm"><span className="text-gray-500">Address:</span> <span className="font-medium">{address}</span></p>}
            <p className="text-sm"><span className="text-gray-500">Postcode:</span> <span className="font-medium">{postcode}</span></p>
          </div>
        </div>

        <Button
          onClick={() => onComplete ? onComplete() : onBack()}
          className="bg-cyo-orange hover:bg-cyo-orange-dark text-white font-semibold px-8 py-5 text-base rounded-xl"
        >
          Compare More Products
        </Button>
      </div>
    );
  }

  // OTP verification screen
  if (step === 'otp') {
    return (
      <div className="text-center py-4">
        <div className="mb-6">
          <div className="w-16 h-16 bg-cyo-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-cyo-orange" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            Verify Your Mobile
          </h2>
          <p className="text-gray-500 text-sm">
            Enter the 6-digit code sent to <span className="font-medium text-gray-700">{formatMobile(verifiedMobile)}</span>
          </p>
        </div>

        <div className="max-w-xs mx-auto space-y-4">
          <Input
            type="text"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-14 text-center text-2xl font-bold tracking-widest border-gray-200 focus:border-cyo-orange focus:ring-cyo-orange rounded-xl"
            maxLength={6}
            data-testid="otp-input"
          />

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <Button
            onClick={handleVerifyOtp}
            disabled={isLoading || otp.length !== 6}
            className="w-full bg-cyo-orange hover:bg-cyo-orange-dark text-white font-semibold py-5 text-base rounded-xl disabled:opacity-70"
            data-testid="verify-otp-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Submit'
            )}
          </Button>

          <button
            onClick={() => setStep('details')}
            className="text-sm text-gray-500 hover:text-cyo-orange transition-colors"
          >
            ← Back to details
          </button>
        </div>
      </div>
    );
  }

  // Contact details form
  return (
    <div className="py-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-cyo-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-cyo-orange" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
          Get Your {productName} Quote
        </h2>
        <p className="text-gray-500 text-sm">
          Enter your details and we'll have a specialist contact you
        </p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        {/* Name fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl p-3">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-2">
              <User className="w-3 h-3 text-cyo-orange" />
              First Name
            </label>
            <Input
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="h-10 border-gray-200 focus:border-cyo-orange focus:ring-cyo-orange rounded-lg bg-white"
              data-testid="first-name-input"
            />
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-2">
              <User className="w-3 h-3 text-cyo-orange" />
              Last Name
            </label>
            <Input
              type="text"
              placeholder="Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="h-10 border-gray-200 focus:border-cyo-orange focus:ring-cyo-orange rounded-lg bg-white"
              data-testid="last-name-input"
            />
          </div>
        </div>

        {/* Email */}
        <div className="bg-gray-50 rounded-xl p-3">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-2">
            <Mail className="w-3 h-3 text-cyo-orange" />
            Email Address
          </label>
          <Input
            type="email"
            placeholder="john@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-10 border-gray-200 focus:border-cyo-orange focus:ring-cyo-orange rounded-lg bg-white"
            data-testid="email-input"
          />
        </div>

        {/* Mobile */}
        <div className="bg-gray-50 rounded-xl p-3">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-2">
            <Phone className="w-3 h-3 text-cyo-orange" />
            Mobile Number
          </label>
          <Input
            type="tel"
            placeholder="0412 345 678"
            value={formatMobile(mobile)}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
            className="h-10 border-gray-200 focus:border-cyo-orange focus:ring-cyo-orange rounded-lg bg-white"
            data-testid="mobile-input"
          />
        </div>

        {/* Location display */}
        <div className="bg-green-50 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-green-600" />
            <span className="text-sm text-gray-700">{address ? 'Address:' : 'Postcode:'}</span>
          </div>
          <span className="font-semibold text-green-700 text-sm text-right max-w-[60%] truncate">
            {address || postcode}
          </span>
        </div>

        {error && <p className="text-red-500 text-sm text-center">{error}</p>}

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-2 border-gray-200 text-gray-600 hover:border-gray-300 font-semibold py-5 rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleSendOtp}
            disabled={isLoading}
            className="flex-[2] bg-cyo-orange hover:bg-cyo-orange-dark text-white font-semibold py-5 rounded-xl disabled:opacity-70"
            data-testid="submit-details-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending Code...
              </>
            ) : (
              'Get My Quote'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
